import { useState } from 'react';
import { Dialog, DialogContent, IconButton } from '@mui/material';
import { Trash2, X } from 'lucide-react';
import l from '../../../../../lang';
import { useDashboard } from '../../../../../store/dashboard';
import { usePayouts } from '../../../../../store/payouts';
import { PayoutContactBankAccount } from '../../../../../store/payouts/interfaces';
import { Button } from '../../../../common/button';
import './styles.scss';

interface DeleteAccountDialogProps {
  open: boolean;
  beneficiaryId: number;
  account: PayoutContactBankAccount | null;
  onClose: () => void;
  onDeleted: () => void;
}

const DeleteAccountDialog = ({
  open,
  beneficiaryId,
  account,
  onClose,
  onDeleted,
}: DeleteAccountDialogProps) => {
  const { merchantMe } = useDashboard();
  const { deletePayoutContactBankAccount } = usePayouts();
  const [submitting, setSubmitting] = useState(false);

  if (!account) {
    return null;
  }

  const handleDelete = () => {
    if (!merchantMe?.id) {
      return;
    }

    setSubmitting(true);
    deletePayoutContactBankAccount(
      String(merchantMe.id),
      String(beneficiaryId),
      String(account.id),
      () => {
        setSubmitting(false);
        onDeleted();
        onClose();
      },
      () => setSubmitting(false)
    );
  };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ className: 'delete-contact-account-dialog' }}
    >
      <IconButton
        className="delete-contact-account-dialog__close"
        aria-label={l('common.close')}
        disabled={submitting}
        onClick={onClose}
      >
        <X size={20} aria-hidden />
      </IconButton>
      <DialogContent className="delete-contact-account-dialog__content">
        <span className="delete-contact-account-dialog__icon" aria-hidden>
          <Trash2 size={24} strokeWidth={1.75} />
        </span>
        <h2>{l('payoutContacts.detail.account.delete.confirm.title')}</h2>
        <p>{l('payoutContacts.detail.account.delete.confirm.body')}</p>
        <div className="delete-contact-account-dialog__actions">
          <Button variant="outlined" disabled={submitting} onClick={onClose}>
            {l('common.cancel')}
          </Button>
          <Button
            variant="contained"
            color="error"
            disabled={submitting || !merchantMe?.id}
            onClick={handleDelete}
          >
            {l('payoutContacts.detail.account.delete')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DeleteAccountDialog;
